import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ExamesAutorizacao.css';
import { bffExecuteInsuranceAction } from '../../functions/apiCalls/bffService';

const UF_OPTIONS = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const normalizeExam = (e) => ({
    id: String(e?.id ?? ''),
    name: String(e?.name ?? e?.nome ?? e?.procedimento ?? '')
});

export default function ExamesAutorizacao({ exames = [], onComplete, isCompleted = false }) {
    const exams = useMemo(() => (Array.isArray(exames) ? exames : []).map(normalizeExam).filter((e) => e.id), [exames]);
    const [doctorIndex, setDoctorIndex] = useState(0);
    const [doctors, setDoctors] = useState([{
        uf: '',
        crm: '',
        examIds: [],
        requestDate: '',
        cidCode: '',
        diagnosisHypothesis: '',
        searched: false,
        doctorName: ''
    }]);
    const [openUf, setOpenUf] = useState(false);
    const ufDropdownRef = useRef(null);
    const [openDate, setOpenDate] = useState(false);
    const dateDropdownRef = useRef(null);
    const today = useMemo(() => new Date(), []);
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [viewYear, setViewYear] = useState(today.getFullYear());

    useEffect(() => {
        const onClickOutside = (e) => {
            if (ufDropdownRef.current && !ufDropdownRef.current.contains(e.target)) {
                setOpenUf(false);
            }
            if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
                setOpenDate(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    // Ao trocar de médico, fechar o dropdown de UF
    useEffect(() => {
        setOpenUf(false);
        setOpenDate(false);
    }, [doctorIndex]);

    const assignedMap = useMemo(() => {
        const m = new Map(); // examId -> doctorIdx
        doctors.forEach((d, idx) => {
            (d.examIds || []).forEach((id) => {
                if (id) m.set(String(id), idx);
            });
        });
        return m;
    }, [doctors]);

    const unassignedExams = useMemo(() => exams.filter((e) => !assignedMap.has(e.id)), [exams, assignedMap]);
    const currentDoctor = doctors[doctorIndex] || doctors[0];
    const [searchingDoctor, setSearchingDoctor] = useState(false);

    const doctorsWithExams = useMemo(
        () => doctors.filter((d) => Array.isArray(d.examIds) && d.examIds.length > 0),
        [doctors]
    );

    // Para cada médico que tenha ao menos um exame vinculado:
    // - UF preenchida com 2 caracteres
    // - CRM preenchido
    const allDoctorsValid = useMemo(
        () =>
            doctorsWithExams.every(
                (d) =>
                    String(d.crm || '').trim().length > 0 &&
                    String(d.uf || '').trim().length === 2
            ),
        [doctorsWithExams]
    );

    // Além de UF/CRM, garantir que para cada médico com exames
    // a API de busca tenha sido acionada ao menos uma vez (searched === true),
    // mesmo que não tenha encontrado o médico.
    const allDoctorsSearched = useMemo(
        () => doctorsWithExams.every((d) => d.searched === true),
        [doctorsWithExams]
    );

    const allAssigned = assignedMap.size === exams.length && exams.length > 0;
    const canContinue = allAssigned && allDoctorsValid && allDoctorsSearched;

    const setDoctorField = (idx, patch) => {
        setDoctors((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
    };

    const handleSearchDoctor = async () => {
        const crm = String(currentDoctor?.crm || '').trim();
        const uf = String(currentDoctor?.uf || '').trim().toUpperCase();
        if (!crm || uf.length !== 2) return;

        try {
            setSearchingDoctor(true);
            const url = process.env.REACT_APP_INSURANCE_API_URL;
            const apiKey = process.env.REACT_APP_INSURANCE_API_KEY;

            const context = {
                action: 'getDoctorHealthBookingAction',
                crmNumber: crm,
                state: uf,
                documentType: 'CRM'
            };

            let data;
            if (process.env.REACT_APP_BFF_API_URL || !apiKey) {
                data = await bffExecuteInsuranceAction(context, '');
            } else {
                const body = {
                    context,
                    code_execute: ''
                };

                const resp = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
                    body: JSON.stringify(body)
                });

                data = resp.ok ? await resp.json() : null;
            }

            const doctorName = String(data?.context?.doctor?.name || '').trim();

            setDoctorField(doctorIndex, {
                searched: true,
                doctorName: doctorName
            });
        } catch (e) {
            setDoctorField(doctorIndex, { searched: true, doctorName: '' });
        } finally {
            setSearchingDoctor(false);
        }
    };

    const formatDdMmYyyy = (d) => {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = String(d.getFullYear());
        return `${dd}/${mm}/${yyyy}`;
    };

    const toggleExamForCurrentDoctor = (examId, checked) => {
        const id = String(examId);
        setDoctors((prev) =>
            prev.map((d, idx) => {
                if (idx !== doctorIndex) return d;
                const ids = new Set(Array.isArray(d.examIds) ? d.examIds.map(String) : []);
                if (checked) ids.add(id);
                else ids.delete(id);
                return { ...d, examIds: Array.from(ids) };
            })
        );
    };

    const addAnotherDoctor = () => {
        setDoctors((prev) => [...prev, {
            uf: '',
            crm: '',
            examIds: [],
            requestDate: '',
            cidCode: '',
            diagnosisHypothesis: '',
            searched: false,
            doctorName: ''
        }]);
        setDoctorIndex((prev) => prev + 1);
    };

    const handleContinue = () => {
        if (!canContinue) return;
        const result = exams
            .map((e) => {
                const idx = assignedMap.get(e.id);
                const d = typeof idx === 'number' ? doctors[idx] : null;
                if (!d) return null;
                return {
                    id: e.id,
                    name: e.name,
                    crm: String(d.crm || '').trim(),
                    uf: String(d.uf || '').trim().toUpperCase(),
                    requestDate: String(d.requestDate || '').trim(),
                    cidCode: String(d.cidCode || '').trim().toUpperCase(),
                    diagnosisHypothesis: String(d.diagnosisHypothesis || '').trim(),
                    notes: ''
                };
            })
            .filter(Boolean);

        if (onComplete) onComplete(result);
    };

    if (isCompleted) {
        return (
            <div className="exaut-container">
                <div className="exaut-card exaut-card--completed">
                    Obrigado! Os dados foram registrados.
                </div>
            </div>
        );
    }

    if (exams.length === 0) return null;

    return (
        <div className="exaut-container">
            <div className="exaut-card">
                <div className="exaut-header">
                    <div className="exaut-title">
                        Alguns procedimentos precisam da <b>autorização do convênio</b>. Por favor, preencha os dados:
                    </div>
                    <div className="exaut-step">
                        Médico {doctorIndex + 1} de {doctors.length}
                    </div>
                </div>

                <div className="exaut-form">
                    <div className="exaut-field-group">
                        <div className="exaut-label">Informe o estado e CRM do médico:</div>
                        <div className="exaut-row">
                            <div className={`exaut-dropdown ${openUf ? 'open' : ''}`} ref={ufDropdownRef}>
                                <button
                                    type="button"
                                    className="exaut-dropdown-control"
                                    onClick={() => setOpenUf((v) => !v)}
                                    aria-expanded={openUf}
                                    aria-label="Selecionar estado (UF)"
                                >
                                    {currentDoctor.uf || 'Estado'}
                                    <span className="exaut-caret" aria-hidden="true" />
                                </button>

                                {openUf && (
                                    <div className="exaut-dropdown-menu" role="listbox" aria-label="Estados">
                                        <div className="exaut-dropdown-list">
                                            {UF_OPTIONS.map((uf) => (
                                                <div
                                                    key={uf}
                                                    className={`exaut-dropdown-item ${currentDoctor.uf === uf ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setDoctorField(doctorIndex, { uf, searched: false, doctorName: '' });
                                                        setOpenUf(false);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    {uf}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <input
                                className="exaut-input"
                                type="text"
                                inputMode="numeric"
                                placeholder="CRM do médico"
                                value={currentDoctor.crm}
                                onChange={(e) => setDoctorField(doctorIndex, {
                                    crm: e.target.value.replace(/\D+/g, ''),
                                    searched: false,
                                    doctorName: ''
                                })}
                                aria-label="CRM do médico"
                            />
                        </div>

                        <div className="exaut-search-row">
                            <button
                                type="button"
                                className="exaut-search-btn"
                                onClick={handleSearchDoctor}
                                disabled={searchingDoctor || String(currentDoctor?.crm || '').trim().length === 0 || String(currentDoctor?.uf || '').trim().length !== 2}
                            >
                                {searchingDoctor ? 'Buscando...' : 'Buscar médico'}
                            </button>
                        </div>

                        {currentDoctor.searched === true && (
                            <div className="exaut-doctor-result">
                                {currentDoctor.doctorName
                                    ? `Médico: ${currentDoctor.doctorName}`
                                    : 'Médico não encontrado. Você pode continuar preenchendo os dados.'}
                            </div>
                        )}
                    </div>

                    <div className="exaut-field-group">
                        <div className="exaut-label">Quais procedimentos o médico prescreveu?</div>
                        <div className="exaut-exams-grid">
                            {exams.map((e) => {
                                const assignedTo = assignedMap.get(e.id);
                                const isAssignedToThis = assignedTo === doctorIndex;
                                const isAssignedToOther = typeof assignedTo === 'number' && assignedTo !== doctorIndex;
                                return (
                                    <label
                                        key={e.id}
                                        className={`exaut-exam ${isAssignedToOther ? 'disabled' : ''}`}
                                        title={isAssignedToOther ? `Já atribuído ao médico ${assignedTo + 1}` : ''}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isAssignedToThis}
                                            disabled={isAssignedToOther}
                                            onChange={(ev) => toggleExamForCurrentDoctor(e.id, ev.target.checked)}
                                        />
                                        <span className="exaut-exam-name">{e.name}</span>
                                    </label>
                                );
                            })}
                        </div>
                        {unassignedExams.length > 0 && (
                            <div className="exaut-hint">
                                Faltam {unassignedExams.length} procedimento(s) para atribuir a um médico.
                            </div>
                        )}
                    </div>

                    <div className="exaut-field-group">
                        <div className="exaut-meta-grid">
                            <div className="exaut-meta-field">
                                <div className={`exaut-dropdown ${openDate ? 'open' : ''}`} ref={dateDropdownRef}>
                                    <button
                                        type="button"
                                        className="exaut-dropdown-control"
                                        onClick={() => setOpenDate((v) => !v)}
                                        aria-expanded={openDate}
                                        aria-label="Selecionar data do pedido (se tiver)"
                                    >
                                        {currentDoctor.requestDate || 'Data do pedido (se tiver)'}
                                        <span className="exaut-caret" aria-hidden="true" />
                                    </button>

                                    {openDate && (
                                        <div className="exaut-dropdown-menu" role="dialog" aria-label="Calendário">
                                            <div className="exaut-calendar-header">
                                                <button
                                                    type="button"
                                                    className="exaut-cal-nav"
                                                    onClick={() => {
                                                        const m = viewMonth === 0 ? 11 : viewMonth - 1;
                                                        const y = viewMonth === 0 ? viewYear - 1 : viewYear;
                                                        setViewMonth(m);
                                                        setViewYear(y);
                                                    }}
                                                    aria-label="Mês anterior"
                                                >
                                                    ‹
                                                </button>
                                                <span className="exaut-cal-title">
                                                    {new Date(viewYear, viewMonth, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="exaut-cal-nav"
                                                    onClick={() => {
                                                        const canNext =
                                                            viewYear < today.getFullYear() ||
                                                            (viewYear === today.getFullYear() && viewMonth < today.getMonth());
                                                        if (!canNext) return;
                                                        const m = viewMonth === 11 ? 0 : viewMonth + 1;
                                                        const y = viewMonth === 11 ? viewYear + 1 : viewYear;
                                                        setViewMonth(m);
                                                        setViewYear(y);
                                                    }}
                                                    aria-label="Próximo mês"
                                                    disabled={
                                                        !(viewYear < today.getFullYear() ||
                                                            (viewYear === today.getFullYear() && viewMonth < today.getMonth()))
                                                    }
                                                >
                                                    ›
                                                </button>
                                            </div>

                                            <div className="exaut-calendar-grid">
                                                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                                                    <div key={`wd-${i}`} className="exaut-calendar-weekday">
                                                        {d}
                                                    </div>
                                                ))}

                                                {(() => {
                                                    const first = new Date(viewYear, viewMonth, 1);
                                                    const start = first.getDay();
                                                    const days = new Date(viewYear, viewMonth + 1, 0).getDate();
                                                    const cells = [];
                                                    for (let i = 0; i < start; i++) cells.push(<div key={`e-${i}`} />);
                                                    for (let day = 1; day <= days; day++) {
                                                        const date = new Date(viewYear, viewMonth, day);
                                                        const val = formatDdMmYyyy(date);
                                                        const isSelected = currentDoctor.requestDate === val;
                                                        const isFuture = date.getTime() > new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
                                                        cells.push(
                                                            <button
                                                                key={`d-${day}`}
                                                                type="button"
                                                                className={`exaut-calendar-day ${isSelected ? 'selected' : ''}`}
                                                                disabled={isFuture}
                                                                onClick={() => {
                                                                    if (isFuture) return;
                                                                    setDoctorField(doctorIndex, { requestDate: val });
                                                                    setOpenDate(false);
                                                                }}
                                                            >
                                                                {day}
                                                            </button>
                                                        );
                                                    }
                                                    return cells;
                                                })()}
                                            </div>

                                            <div className="exaut-cal-footer">
                                                <button
                                                    type="button"
                                                    className="exaut-cal-action"
                                                    onClick={() => setDoctorField(doctorIndex, { requestDate: '' })}
                                                >
                                                    Limpar
                                                </button>
                                                <button
                                                    type="button"
                                                    className="exaut-cal-action"
                                                    onClick={() => {
                                                        const val = formatDdMmYyyy(new Date());
                                                        setViewMonth(today.getMonth());
                                                        setViewYear(today.getFullYear());
                                                        setDoctorField(doctorIndex, { requestDate: val });
                                                        setOpenDate(false);
                                                    }}
                                                >
                                                    Hoje
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="exaut-meta-field">
                                <input
                                    className="exaut-input"
                                    type="text"
                                    placeholder="CID (se tiver)"
                                    value={currentDoctor.cidCode}
                                    onChange={(e) => setDoctorField(doctorIndex, { cidCode: e.target.value.toUpperCase() })}
                                    aria-label="CID (se tiver)"
                                />
                            </div>
                        </div>

                        <div className="exaut-meta-field">
                            <input
                                className="exaut-input"
                                type="text"
                                placeholder="HD (se tiver)"
                                value={currentDoctor.diagnosisHypothesis}
                                onChange={(e) => setDoctorField(doctorIndex, { diagnosisHypothesis: e.target.value })}
                                aria-label="HD (se tiver)"
                            />
                        </div>
                    </div>
                </div>

                <div className="exaut-footer">
                    <div className="exaut-footer-left">
                        <button
                            type="button"
                            className="exaut-link"
                            onClick={addAnotherDoctor}
                            disabled={unassignedExams.length === 0}
                        >
                            + Adicionar outro médico
                        </button>

                        <div className="exaut-carousel">
                            <button
                                type="button"
                                className="exaut-carousel-btn"
                                onClick={() => setDoctorIndex((i) => Math.max(0, i - 1))}
                                disabled={doctorIndex === 0}
                                aria-label="Médico anterior"
                            >
                                ‹
                            </button>
                            <div className="exaut-carousel-dots" aria-label="Navegação de médicos">
                                {doctors.map((_, i) => (
                                    <button
                                        key={`doc-dot-${i}`}
                                        type="button"
                                        className={`exaut-dot ${i === doctorIndex ? 'active' : ''}`}
                                        onClick={() => setDoctorIndex(i)}
                                        aria-label={`Ir para médico ${i + 1}`}
                                    />
                                ))}
                            </div>
                            <button
                                type="button"
                                className="exaut-carousel-btn"
                                onClick={() => setDoctorIndex((i) => Math.min(doctors.length - 1, i + 1))}
                                disabled={doctorIndex === doctors.length - 1}
                                aria-label="Próximo médico"
                            >
                                ›
                            </button>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={`exaut-primary ${canContinue ? '' : 'disabled'}`}
                        onClick={handleContinue}
                        disabled={!canContinue}
                    >
                        Continuar
                    </button>
                </div>
            </div>
        </div>
    );
}
